import { merge } from 'lodash-es';
import * as yup from 'yup';
import { StringListCellRenderer } from '../components/AgTable/cell-renderers/StringListCellRenderer';
import { RendererType } from '../data-models/field-definition';
import { ParticipationType } from '../data-models/scenario.data-model';
import { PpsFormatterDataModel } from '@foresightdata/web-common-core';
import {
  dateField,
  integerField,
  multiplierField,
  percentField,
  pricePerShareField,
  usdField,
} from './common-schema-defs';
import { companyGridSchema } from './Company.schema';

export function securitySchema() {
  return yup.object().shape(securityFields());
}

export function securityFields() {
  return {
    captableId: yup.number().required(),
    coInvestors: yup.array().of(yup.string()).nullable().label('Co-Investors').default([]),
    companyId: yup.number().required().label('Company').customMeta({
      renderer: RendererType.companyId,
    }),
    conversionPrice: pricePerShareField().nullable().label('Conversion PPS'),
    customData: yup.object().default({}).nullable(),
    discountRate: percentField().default(0).nullable().label('Discount %'),
    displayName: yup.string().required().label('Security').default(''),
    fundIds: yup.array().of(yup.number()).nullable().default([]).label('Fund'),
    id: yup.number().required(),
    interestRate: percentField().default(0).nullable().label('Interest Rate %'),
    investedAmount: usdField().nullable(),
    investmentDate: dateField().nullable(),
    liquidationMultiplier: multiplierField().default(0).nullable(),
    name: yup.string().required(),
    ownershipPercentage: percentField().default(0).nullable().label('Ownership % of Event'),
    paidInCapital: usdField().nullable(),
    participationCap: multiplierField().nullable(),
    participationType: yup.string().oneOf(Object.values(ParticipationType)).nullable(),
    postMoneyValuation: usdField().nullable(),
    preMoneyValuation: usdField().nullable(),
    pricePerShare: pricePerShareField().nullable().label('PPS'),
    raisedAmount: usdField().nullable(),
    seniority: integerField().nullable(),
    sharesHeld: integerField().nullable(),
    sharesIssued: integerField().nullable().label('Total FDS'),
    valuationCap: usdField().nullable(),
  };
}

export type SecurityDataModel = yup.InferType<ReturnType<typeof securitySchema>>;

export function createSecurityDataModel(overrides: Partial<SecurityDataModel> = {}): SecurityDataModel {
  return merge(
    securitySchema().getDefault(),
    {
      captableId: 0,
      companyId: 0,
      id: 0,
      name: '',
    },
    overrides
  );
}

export function securityGridSchema() {
  const {
    coInvestors,
    companyId,
    conversionPrice,
    investedAmount,
    liquidationMultiplier,
    paidInCapital,
    participationCap,
    pricePerShare,
    postMoneyValuation,
    preMoneyValuation,
    raisedAmount,
    sharesHeld,
    sharesIssued,
    valuationCap,
    ...rest
  } = securityFields();

  return yup.object().shape({
    ...rest,
    coInvestors: coInvestors.gridMeta({
      cellRenderer: StringListCellRenderer,
    }),
    companyId: companyId.gridMeta({
      renderer: RendererType.companyId,
    }),
    company: companyGridSchema(),
    conversionPrice: conversionPrice.gridMeta({ formatter: PpsFormatterDataModel }),
    fundType: yup.array().of(yup.string()).label('Fund Group'),
    investedAmount: investedAmount.gridMeta({ formatter: 'currency', aggFunc: 'sum' }),
    liquidationMultiplier: liquidationMultiplier.gridMeta({ formatter: 'multiplier' }),
    paidInCapital: paidInCapital.gridMeta({ formatter: 'currency', aggFunc: 'sum' }),
    participationCap: participationCap.gridMeta({ formatter: 'multiplier' }),
    postMoneyValuation: postMoneyValuation.gridMeta({ formatter: 'currency' }),
    preMoneyValuation: preMoneyValuation.gridMeta({ formatter: 'currency' }),
    pricePerShare: pricePerShare.gridMeta({ formatter: PpsFormatterDataModel }),
    raisedAmount: raisedAmount.gridMeta({ formatter: 'currency', aggFunc: 'sum' }),
    sharesHeld: sharesHeld.gridMeta({ aggFunc: 'sum' }),
    sharesIssued: sharesIssued.gridMeta({ aggFunc: 'sum' }),
    valuationCap: valuationCap.gridMeta({ formatter: 'currency' }),
  });
}
